// reCAPTCHA Settings Manager
import React, { useState, useEffect } from 'react';
import { Loader2, Save, Shield, Check, X, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RecaptchaManager = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    has_site_key: false,
    has_secret_key: false
  });
  const [newKeys, setNewKeys] = useState({
    site_key: '',
    secret_key: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/recaptcha-settings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      console.error('Error fetching reCAPTCHA settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/recaptcha-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          enabled: settings.enabled,
          site_key: newKeys.site_key || undefined,
          secret_key: newKeys.secret_key || undefined
        })
      });
      
      if (response.ok) {
        setHasChanges(false);
        setNewKeys({ site_key: '', secret_key: '' });
        fetchSettings(); // Refresh to see updated has_* flags
        alert('Paramètres reCAPTCHA enregistrés !');
      } else {
        const err = await response.json();
        alert(err.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error('Error saving reCAPTCHA settings:', err);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (value) => {
    setSettings(prev => ({ ...prev, enabled: value }));
    setHasChanges(true);
  };

  const handleKeyChange = (field, value) => {
    setNewKeys(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-light text-slate-800">reCAPTCHA</h2>
          <p className="text-sm text-slate-500 mt-1">Protection anti-robot sur les formulaires</p>
        </div>
        <Button onClick={handleSave} disabled={saving || !hasChanges}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      {/* Enable/Disable */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${settings.enabled ? 'bg-green-100' : 'bg-slate-100'}`}>
              <Shield className={`w-6 h-6 ${settings.enabled ? 'text-green-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="font-medium text-slate-800">Protection reCAPTCHA</h3>
              <p className="text-sm text-slate-500">Activer la vérification anti-robot sur les formulaires</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.enabled || false}
              onChange={() => handleToggle(!settings.enabled)}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
          </label>
        </div>
      </div>

      {/* Keys Status */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <h3 className="font-medium text-slate-800 mb-4">État des clés</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <span className="text-sm text-slate-600">Clé du site (Site Key)</span>
            {settings.has_site_key ? (
              <span className="flex items-center gap-1 text-green-600 text-sm"><Check className="w-4 h-4" /> Configurée</span>
            ) : (
              <span className="flex items-center gap-1 text-red-500 text-sm"><X className="w-4 h-4" /> Non configurée</span>
            )}
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <span className="text-sm text-slate-600">Clé secrète (Secret Key)</span>
            {settings.has_secret_key ? (
              <span className="flex items-center gap-1 text-green-600 text-sm"><Check className="w-4 h-4" /> Configurée</span>
            ) : (
              <span className="flex items-center gap-1 text-red-500 text-sm"><X className="w-4 h-4" /> Non configurée</span>
            )}
          </div>
        </div>
      </div>

      {/* Update Keys */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
        <h3 className="font-medium text-slate-800">Mettre à jour les clés</h3>
        <p className="text-sm text-slate-500 -mt-4">Les clés ne sont jamais affichées. Entrez de nouvelles valeurs pour les remplacer.</p>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Nouvelle clé du site (Site Key)
          </label>
          <input
            type="text"
            value={newKeys.site_key}
            onChange={(e) => handleKeyChange('site_key', e.target.value)}
            placeholder="6LcXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
            className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm font-mono"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Nouvelle clé secrète (Secret Key)
          </label>
          <input
            type="password"
            value={newKeys.secret_key}
            onChange={(e) => handleKeyChange('secret_key', e.target.value)}
            placeholder="6LcXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
            className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm font-mono"
          />
        </div>
      </div>

      {/* Help */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <h4 className="font-medium text-blue-900 mb-2">Comment obtenir les clés ?</h4>
        <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
          <li>Allez sur <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">Google reCAPTCHA Admin <ExternalLink className="w-3 h-3" /></a></li>
          <li>Créez un nouveau site avec le type <strong>"reCAPTCHA v2 - Case à cocher"</strong></li>
          <li>Ajoutez vos domaines (ex: sae.finxx24.click, localhost)</li>
          <li>Copiez les clés et collez-les ci-dessus</li>
        </ol>
      </div>

      {/* Status */}
      {settings.enabled && settings.has_site_key && settings.has_secret_key && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <Shield className="w-5 h-5 text-green-600" />
          <span className="text-green-800 text-sm">reCAPTCHA est actif sur les formulaires de contact et partenariat</span>
        </div>
      )}

      {settings.enabled && (!settings.has_site_key || !settings.has_secret_key) && (
        <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-center gap-3">
          <Shield className="w-5 h-5 text-orange-600" />
          <span className="text-orange-800 text-sm">Veuillez configurer les deux clés pour activer la protection</span>
        </div>
      )}
    </div>
  );
};

export default RecaptchaManager;
